import { useState } from 'react'

import { useFormContext, useWatch } from 'react-hook-form'
import { useQuery } from 'react-query'

import type { SelectOptionsType } from '@/components/ui/form/FormSelect.tsx'
import type { Creator } from '@/modules/creators/types'
import { handleError } from '@/utils/error'

import { getAccessibleTeamMembersForCreators } from '../../data/service'
import type { ShiftFormValues } from './types'

export const useShiftForm = (creators: Creator[], isEdit = false) => {
  const { getValues, setValue } = useFormContext<ShiftFormValues>()
  const creatorIds = isEdit
    ? [Number(getValues('creatorId'))]
    : getValues('creatorIds')?.map(Number)
  // It was needed as a workaround for the bug BUG-510/5
  const [creatorIdsToRequestMembers, setCreatorIdsToRequestMembers] = useState<
    number[] | undefined
  >(creatorIds)

  const accessibleTeamMembersQuery = useQuery(
    ['availableTeamMemberIds', creatorIdsToRequestMembers],
    () =>
      creatorIdsToRequestMembers?.length
        ? getAccessibleTeamMembersForCreators(creatorIdsToRequestMembers)
        : undefined,
    {
      enabled: !!creatorIdsToRequestMembers?.length,
      onError: (error) =>
        handleError(
          error,
          'Failed to get accessible team members',
          accessibleTeamMembersQuery.refetch,
        ),
    },
  )

  const memberOptions =
    accessibleTeamMembersQuery.data?.members?.reduce<SelectOptionsType>(
      (options, { id, name, status }) => {
        if (status === 'Active') {
          options.push({
            label: name || '',
            value: String(id),
          })
        }

        return options
      },
      [],
    ) ?? []

  const creatorsOptions = creators
    ?.map(({ id, name, licenseId }) => ({
      label: name,
      value: String(id),
      disabled: !licenseId,
      tooltip: !licenseId ? "Creator doesn't have a license" : undefined,
    }))
    .sort((a, b) => Number(a.disabled) - Number(b.disabled))

  const handleChangeCreatorIds = (value: string[]) => {
    setCreatorIdsToRequestMembers(value.map(Number))
    setValue('memberIds', [])
  }

  useWatch<ShiftFormValues>({ name: 'startedAt' })
  useWatch<ShiftFormValues>({ name: 'recurring' })

  return {
    memberOptions,
    creatorsOptions,
    handleChangeCreatorIds,
  }
}
